/**
 * Role Terminology Types
 *
 * Organization-specific role configurations that map to system roles.
 * Allows organizations to customize role names, descriptions, and access settings.
 *
 * System Roles (6 core roles for permission levels):
 * - super_admin: Platform-wide administration
 * - org_admin: Organization administration
 * - program_manager: Program administration and reporting
 * - supervisor: Team supervision and approvals
 * - staff: Standard member/enrollment access
 * - viewer: Read-only access
 *
 * Organization roles (like 'promotora', 'care_coordinator', 'CHW', 'navigator')
 * are configured here and map to one of the 6 system roles.
 */

import { CBORoleType, AllRoleTypes } from './cboRoles';

// ============================================================================
// SYSTEM ROLES
// ============================================================================

/**
 * Valid system role values (6 core roles)
 */
export const VALID_SYSTEM_ROLES: CBORoleType[] = [
  'super_admin',
  'org_admin',
  'program_manager',
  'supervisor',
  'staff',
  'viewer',
];

/**
 * System role information from database
 */
export interface SystemRoleInfo {
  role_key: CBORoleType;
  display_name: string;
  display_name_es?: string;
  description?: string;
  description_es?: string;
  access_level: number;
  can_be_restricted: boolean;
  is_admin_role: boolean;
}

// ============================================================================
// ORGANIZATION ROLE TYPES
// ============================================================================

/**
 * Organization role (enhanced role_terminology)
 * Maps to a system role for permission inheritance
 */
export interface OrganizationRole {
  id: number;
  organization_id: number;
  role_type: string;              // Unique key within org (e.g., 'promotora', 'chw', 'navigator')
  display_name_en: string;
  display_name_es?: string;
  description_en?: string;
  description_es?: string;
  system_role: CBORoleType;       // Maps to system role for permissions
  abbreviation?: string;          // Short code (e.g., 'CHW', 'PN')
  icon?: string;                  // Lucide icon name
  color?: string;                 // Badge color
  is_field_worker: boolean;       // Show simplified UI
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at?: string;
  // Joined system role info
  system_role_display_name?: string;
  access_level?: number;
  can_be_restricted?: boolean;
}

/**
 * Create/Update payload for organization role
 */
export interface OrganizationRoleCreate {
  role_type: string;
  display_name_en: string;
  display_name_es?: string;
  description_en?: string;
  description_es?: string;
  system_role: CBORoleType;
  abbreviation?: string;
  icon?: string;
  color?: string;
  is_field_worker?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

/**
 * Update payload for organization role
 */
export interface OrganizationRoleUpdate {
  display_name_en?: string;
  display_name_es?: string;
  description_en?: string;
  description_es?: string;
  system_role?: CBORoleType;
  abbreviation?: string;
  icon?: string;
  color?: string;
  is_field_worker?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

// ============================================================================
// ROLE-PROGRAM ACCESS
// ============================================================================

/**
 * Access level for role-program assignment
 */
export type ProgramAccessLevel = 'full' | 'view_only' | 'none';

/**
 * Program access configuration for an organization role
 */
export interface RoleProgramAccess {
  program_id: number;
  program_name?: string;
  access_level: ProgramAccessLevel;
}

/**
 * Bulk update payload for role program access
 */
export interface RoleProgramAccessUpdate {
  programs: RoleProgramAccess[];
}

// ============================================================================
// ROLE PAGE PERMISSIONS
// ============================================================================

/**
 * Page permission with view/edit granularity
 */
export interface RolePagePermission {
  page_key: string;
  page_name?: string;
  page_name_es?: string;
  category?: string;
  access_level: 'enabled' | 'disabled' | 'inherit';
  view_access: boolean;
  edit_access: boolean;
  delete_access: boolean;
  approve_access: boolean;
  export_access: boolean;
}

/**
 * Bulk update payload for role page permissions
 */
export interface RolePagePermissionsUpdate {
  permissions: RolePagePermission[];
}

// ============================================================================
// EFFECTIVE PERMISSIONS
// ============================================================================

/**
 * Effective permissions for an organization role
 */
export interface EffectiveRolePermissions {
  role_type: string;
  system_role: CBORoleType;
  access_level: number;
  is_admin_role: boolean;
  default_permissions: Record<string, string[]>;
  program_access: Array<{
    id: number;
    name: string;
    access_level: ProgramAccessLevel;
  }>;
  page_access: Array<{
    page_key: string;
    page_name: string;
    access_level: string;
    view_access: boolean;
    edit_access: boolean;
  }>;
}

// ============================================================================
// LEGACY SUPPORT (Backward Compatibility)
// ============================================================================

/**
 * Role terminology mapping for a specific organization
 * @deprecated Use OrganizationRole instead
 */
export interface RoleTerminologyMapping {
  id: number;
  organization_id: number;
  system_role: AllRoleTypes;
  display_name: string;
  display_name_es: string;
  abbreviation?: string;
  description?: string;
  description_es?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

/**
 * Create/Update payload for role terminology
 * @deprecated Use OrganizationRoleCreate instead
 */
export interface RoleTerminologyCreate {
  system_role: AllRoleTypes;
  display_name: string;
  display_name_es: string;
  abbreviation?: string;
  description?: string;
  description_es?: string;
  is_active?: boolean;
}

// ============================================================================
// DEFAULT ROLE TERMINOLOGY
// ============================================================================

/**
 * Default role display names for system roles (6 core roles)
 */
export const DEFAULT_ROLE_TERMINOLOGY: Record<CBORoleType, { en: string; es: string; abbr: string }> = {
  super_admin: { en: 'Super Administrator', es: 'Super Administrador', abbr: 'SA' },
  org_admin: { en: 'Organization Administrator', es: 'Administrador de Organización', abbr: 'OA' },
  program_manager: { en: 'Program Manager', es: 'Gerente de Programa', abbr: 'PM' },
  supervisor: { en: 'Supervisor', es: 'Supervisor', abbr: 'Sup' },
  staff: { en: 'Staff', es: 'Personal', abbr: 'Staff' },
  viewer: { en: 'Viewer', es: 'Observador', abbr: 'View' },
};

/**
 * Common alternative terminology options for field worker roles
 * These map to 'staff' system role
 */
export const COMMON_FIELD_WORKER_TERMS: Array<{ en: string; es: string; abbr: string; icon?: string; color?: string }> = [
  { en: 'Promotora', es: 'Promotora', abbr: 'Pro', icon: 'UserHeart', color: 'green' },
  { en: 'Community Health Worker', es: 'Trabajador de Salud Comunitaria', abbr: 'CHW', icon: 'Heart', color: 'green' },
  { en: 'Patient Navigator', es: 'Navegador de Pacientes', abbr: 'PN', icon: 'Compass', color: 'teal' },
  { en: 'Health Navigator', es: 'Navegador de Salud', abbr: 'HN', icon: 'Compass', color: 'teal' },
  { en: 'Care Coordinator', es: 'Coordinador de Cuidado', abbr: 'CC', icon: 'Heart', color: 'pink' },
  { en: 'Outreach Worker', es: 'Trabajador de Alcance', abbr: 'OW', icon: 'Users', color: 'blue' },
  { en: 'Peer Support Specialist', es: 'Especialista de Apoyo entre Pares', abbr: 'PSS', icon: 'UserCheck', color: 'purple' },
  { en: 'Community Liaison', es: 'Enlace Comunitario', abbr: 'CL', icon: 'Link', color: 'indigo' },
  { en: 'Field Agent', es: 'Agente de Campo', abbr: 'FA', icon: 'MapPin', color: 'orange' },
  { en: 'Case Worker', es: 'Trabajador Social', abbr: 'CW', icon: 'Briefcase', color: 'purple' },
  { en: 'Enrollment Specialist', es: 'Especialista de Inscripción', abbr: 'ES', icon: 'FileSignature', color: 'amber' },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get display name for a role, using org terminology or system default
 */
export function getRoleDisplayName(
  role: AllRoleTypes | string,
  language: 'en' | 'es',
  orgRoles?: OrganizationRole[]
): string {
  // Check org-specific roles first
  if (orgRoles) {
    const orgRole = orgRoles.find(r => r.role_type === role && r.is_active);
    if (orgRole) {
      return language === 'es' ? (orgRole.display_name_es || orgRole.display_name_en) : orgRole.display_name_en;
    }
  }

  // Check if it's a system role
  const systemRole = DEFAULT_ROLE_TERMINOLOGY[role as CBORoleType];
  if (systemRole) {
    return language === 'es' ? systemRole.es : systemRole.en;
  }

  // Return role as-is for unknown roles
  return role;
}

/**
 * Get abbreviation for a role
 */
export function getRoleAbbreviation(
  role: AllRoleTypes | string,
  orgRoles?: OrganizationRole[]
): string {
  // Check org-specific roles first
  if (orgRoles) {
    const orgRole = orgRoles.find(r => r.role_type === role && r.is_active);
    if (orgRole?.abbreviation) {
      return orgRole.abbreviation;
    }
  }

  // Check system role defaults
  const systemRole = DEFAULT_ROLE_TERMINOLOGY[role as CBORoleType];
  if (systemRole?.abbr) {
    return systemRole.abbr;
  }

  // Generate abbreviation from role name
  return role.substring(0, 3).toUpperCase();
}

/**
 * Get the system role for an organization role
 */
export function getSystemRoleForOrgRole(
  roleType: string,
  orgRoles?: OrganizationRole[]
): CBORoleType {
  // Check org-specific roles first
  if (orgRoles) {
    const orgRole = orgRoles.find(r => r.role_type === roleType && r.is_active);
    if (orgRole?.system_role && VALID_SYSTEM_ROLES.includes(orgRole.system_role)) {
      return orgRole.system_role;
    }
  }

  // Check if it's already a system role
  if (VALID_SYSTEM_ROLES.includes(roleType as CBORoleType)) {
    return roleType as CBORoleType;
  }

  // Default legacy mappings
  if (roleType === 'promotora' || roleType === 'care_coordinator') {
    return 'staff';
  }

  // Default to staff
  return 'staff';
}

/**
 * Check if a role is a field worker role (should use simplified UI)
 */
export function isFieldWorkerRole(
  roleType: string,
  orgRoles?: OrganizationRole[]
): boolean {
  if (orgRoles) {
    const orgRole = orgRoles.find(r => r.role_type === roleType && r.is_active);
    if (orgRole) {
      return orgRole.is_field_worker;
    }
  }
  // Legacy field worker roles
  return roleType === 'promotora';
}

/**
 * Get role icon
 */
export function getRoleIcon(
  roleType: string,
  orgRoles?: OrganizationRole[]
): string | undefined {
  if (orgRoles) {
    const orgRole = orgRoles.find(r => r.role_type === roleType && r.is_active);
    return orgRole?.icon;
  }
  return undefined;
}

/**
 * Get role color
 */
export function getRoleColor(
  roleType: string,
  orgRoles?: OrganizationRole[]
): string | undefined {
  if (orgRoles) {
    const orgRole = orgRoles.find(r => r.role_type === roleType && r.is_active);
    return orgRole?.color;
  }
  return undefined;
}

// ============================================================================
// USER PROGRAM ASSIGNMENT TYPES (Enhanced)
// ============================================================================

/**
 * Role capabilities configuration
 */
export interface RoleCapabilities {
  can_supervise_team: boolean;
  can_manage_programs: boolean;
  can_view_all_members: boolean;
  can_approve_milestones: boolean;
  can_export_data: boolean;
}

/**
 * Organization role with capabilities for program assignments
 */
export interface AssignableRole {
  id: number;
  role_type: string;
  display_name: string;
  display_name_es?: string;
  description?: string;
  system_role: CBORoleType;
  abbreviation?: string;
  icon?: string;
  color?: string;
  is_field_worker: boolean;
  sort_order: number;
  capabilities: RoleCapabilities;
  role_capabilities?: Record<string, unknown>;
}

/**
 * Organization role basic info (embedded in assignments)
 */
export interface OrganizationRoleBasic {
  id: number;
  role_type: string;
  display_name: string;
  system_role: CBORoleType;
  icon?: string;
  color?: string;
  is_field_worker: boolean;
  capabilities: RoleCapabilities;
  role_capabilities?: Record<string, unknown>;
}

/**
 * User program assignment with enhanced role and permission support
 */
export interface UserProgramAssignment {
  id: number;
  program_id: number;
  program_name: string;
  program_role?: string;  // Legacy field (deprecated)
  organization_role_id?: number;
  organization_role?: OrganizationRoleBasic;
  is_program_lead: boolean;
  can_edit_program: boolean;
  can_enroll_members: boolean;
  can_manage_members: boolean;
  permissions_override?: Record<string, boolean>;
  notes?: string;
  assigned_at: string;
}

/**
 * Create payload for user program assignment
 */
export interface UserProgramAssignmentCreate {
  program_id: number;
  program_role?: string;  // Legacy field (deprecated)
  organization_role_id?: number;
  is_program_lead?: boolean;
  can_edit_program?: boolean;
  can_enroll_members?: boolean;
  can_manage_members?: boolean;  // Default: true (edit capability)
  permissions_override?: Record<string, boolean>;
  notes?: string;
}

/**
 * Update payload for changing program role
 */
export interface ProgramRoleChange {
  organization_role_id: number;
  is_program_lead?: boolean;
  can_edit_program?: boolean;
  can_enroll_members?: boolean;
  can_manage_members?: boolean;
  permissions_override?: Record<string, boolean>;
}

/**
 * User's effective permissions across the organization
 */
export interface UserEffectivePermissions {
  user_id: number;
  organization_id: number;
  has_access: boolean;
  is_admin: boolean;
  base_role?: string;
  system_role?: CBORoleType;
  role_display_name?: string;
  global_permissions: Record<string, boolean>;
  program_permissions: Record<number, Record<string, unknown>>;
}

/**
 * Program lead information
 */
export interface ProgramLead {
  user_id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  name: string;
  organization_role_id?: number;
  role_name?: string;
  can_edit_program: boolean;
  can_enroll_members: boolean;
  can_manage_members: boolean;
}
